import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { PaymentTermsFields, emptyPaymentTerms, paymentTermsToInput, type PaymentTerms } from "../purchasing/PaymentTermsFields";
import { PriceHistoryTable } from "../purchasing/PriceHistoryTable";
import { trpc } from "../../lib/trpc";
import type { OverviewItem } from "./StockOverviewSection";

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
/** TR sayı biçimi: 12.500 → 12500 · 1.234,56 → 1234.56 (ShipmentDetailDrawer.parseTrNumber ile aynı). */
function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}

const REASONS: Array<{ code: string; label: string }> = [
  { code: "sayim_farki", label: "Sayım farkı" },
  { code: "fire", label: "Fire / kayıp" },
  { code: "hasar", label: "Hasar / bozulma" },
  { code: "diger", label: "Diğer" },
];

// ── STOK DÜZELTME ────────────────────────────────────────────────────────
// Mevcut inventory.adjustment mutation'ının ilk UI'sı: lot+lokasyon seç, ± miktar, sebep.
export function StockAdjustmentDrawer({ open, item, onClose, onDone }: {
  open: boolean;
  item: OverviewItem | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [rowKey, setRowKey] = useState(""); // `${lotId}:${locationId}`
  const [direction, setDirection] = useState<"increase" | "decrease">("decrease");
  const [qtyStr, setQtyStr] = useState("");
  const [reason, setReason] = useState(REASONS[0]!.code);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const balQ = trpc.inventory.balanceByItem.useQuery(
    { itemKind: item?.kind ?? "stock", itemId: item?.id ?? "" },
    { enabled: open && !!item },
  );
  const adjMut = trpc.inventory.adjustment.useMutation();

  useEffect(() => {
    if (open) { setRowKey(""); setDirection("decrease"); setQtyStr(""); setReason(REASONS[0]!.code); setNotes(""); setErr(null); adjMut.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const balRows = balQ.data?.rows ?? [];
  const rowOptions = useMemo<SSOption[]>(() => balRows.map((b) => ({
    value: `${b.lotId}:${b.locationId}`,
    label: `${b.lotCode} · ${b.locationName}`,
    desc: b.expiresAt ? `SKT ${new Intl.DateTimeFormat("tr-TR").format(new Date(b.expiresAt))}` : undefined,
    tail: `${fmtQty(b.qty)} ${b.uom}`,
  })), [balRows]);
  const selected = useMemo(() => balRows.find((b) => `${b.lotId}:${b.locationId}` === rowKey) ?? null, [balRows, rowKey]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!item) return;
    if (!selected) return setErr("Düzeltilecek lot + lokasyon seçin.");
    const n = qtyStr.trim() ? parseTrNumber(qtyStr) : NaN;
    if (!Number.isFinite(n) || n <= 0) return setErr("Geçerli bir miktar girin (TR biçimi: 12,5 veya 1.234,56).");
    if (direction === "decrease" && selected.qty <= 0) return setErr("Bu satırın bakiyesi zaten sıfırın altında — yalnız Artır (+) ile sayım düzeltmesi yapılabilir.");
    if (direction === "decrease" && n > selected.qty) return setErr(`Azaltma, seçili satır bakiyesini (${fmtQty(selected.qty)} ${selected.uom}) aşamaz — negatif stok engelli.`);
    const signed = direction === "decrease" ? -n : n;
    try {
      await adjMut.mutateAsync({
        lotId: selected.lotId,
        locationId: selected.locationId,
        qty: signed,
        reasonCode: reason,
        notes: notes.trim() || undefined,
      });
      await qc.invalidateQueries();
      onDone(`${item.name}: ${selected.lotCode} · ${direction === "decrease" ? "−" : "+"}${fmtQty(n)} ${selected.uom} düzeltme kaydedildi.`);
      onClose();
    } catch { /* hata banner'ı */ }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Stok Düzeltme"
      subtitle={item ? `${item.name} · ${item.code}` : undefined}
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose} disabled={adjMut.isPending}>İptal</button>
        <button type="submit" form="adj-form" className="btn-md primary" disabled={adjMut.isPending || balRows.length === 0}>
          {adjMut.isPending ? "Kaydediliyor…" : "Düzeltmeyi Kaydet"}
        </button>
      </>}
    >
      <form id="adj-form" onSubmit={handleSubmit} noValidate>
        {err || adjMut.error ? <div className="form-banner-err" role="alert">{err ?? adjMut.error?.message}</div> : null}
        <div className="form-banner-info">
          Düzeltme, seçilen lot + lokasyon bakiyesine işaretli bir <b>adjustment</b> hareketi yazar; geçmiş silinmez, iz kalır.
        </div>

        {balQ.isLoading ? (
          <div style={{ padding: 16, color: "var(--neutral-500)", fontSize: 13 }}>Bakiyeler yükleniyor…</div>
        ) : balRows.length === 0 ? (
          <div style={{ padding: 16, color: "var(--neutral-500)", fontSize: 13 }}>
            Bu ürünün stokta bakiyesi yok. Artış girişi için <b>Mal Kabul</b> kullanın (düzeltme mevcut lot ister).
          </div>
        ) : (
          <>
            <div className="form-field">
              <label className="lbl" htmlFor="adj-row">Lot + Lokasyon <span className="req">*</span></label>
              <SearchSelect
                id="adj-row"
                value={rowKey || null}
                options={rowOptions}
                onChange={(v) => setRowKey(v ?? "")}
                placeholder="— Seçin —"
                searchPlaceholder="Lot / lokasyon ara…"
              />
              {selected ? <span className="help">Seçili satır bakiyesi: <b className="mono">{fmtQty(selected.qty)} {selected.uom}</b></span> : null}
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="adj-dir">Yön <span className="req">*</span></label>
                <select id="adj-dir" className="form-select" value={direction} onChange={(e) => setDirection(e.target.value as "increase" | "decrease")}>
                  <option value="decrease">Azalt (−) · fire, kayıp, sayım eksiği</option>
                  <option value="increase">Artır (+) · sayım fazlası</option>
                </select>
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="adj-qty">Miktar <span className="req">*</span></label>
                <input id="adj-qty" className="form-input" type="text" inputMode="decimal" value={qtyStr}
                  onChange={(e) => { setQtyStr(e.target.value.replace(/[^\d.,]/g, "")); setErr(null); }}
                  placeholder={selected ? `en çok ${fmtQty(selected.qty)}` : "örn. 12,5"} />
                <span className="help">Ürünün temel biriminde{item ? ` (${item.uom})` : ""}.</span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="adj-reason">Sebep <span className="req">*</span></label>
                <select id="adj-reason" className="form-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {REASONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="adj-notes">Not</label>
                <input id="adj-notes" className="form-input" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={300} placeholder="opsiyonel açıklama" />
              </div>
            </div>
          </>
        )}
      </form>
    </Drawer>
  );
}

// ── SATIN ALMA TALEBİ ────────────────────────────────────────────────────
// Yalnız ham madde: tek kalemlik TASLAK PO (purchasing.createPosFromSuggestions) — MRP akışıyla aynı motor.
export function PurchaseRequestDrawer({ open, item, onClose, onDone }: {
  open: boolean;
  item: OverviewItem | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [qtyStr, setQtyStr] = useState("");
  const [unitPriceStr, setUnitPriceStr] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [requesterId, setRequesterId] = useState("");
  const [pt, setPt] = useState<PaymentTerms>(emptyPaymentTerms);
  const [err, setErr] = useState<string | null>(null);
  const [donePo, setDonePo] = useState<{ code: string; id: string } | null>(null);

  const cardQ = trpc.stockCard.get.useQuery({ id: item?.id ?? "" }, { enabled: open && !!item && item.kind === "stock" });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { enabled: open, staleTime: 60_000 });
  const usersQ = trpc.purchasing.assignableUsers.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const createMut = trpc.purchasing.createPosFromSuggestions.useMutation();

  // Önerilen miktar: eşik − eldeki (eşik yoksa boş bırak, kullanıcı girer).
  const suggestedQty = useMemo(() => {
    if (!item) return null;
    if (item.reorderPoint != null && item.reorderPoint > item.onHand) return item.reorderPoint - item.onHand;
    return null;
  }, [item]);

  useEffect(() => {
    if (open) {
      setQtyStr(suggestedQty != null ? String(suggestedQty).replace(".", ",") : "");
      setUnitPriceStr(""); setSupplierId(""); setRequesterId(""); setPt(emptyPaymentTerms); setErr(null); setDonePo(null); createMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  // Kartın varsayılan tedarikçisi yüklenince (kullanıcı seçmediyse) ön-seç.
  const defaultSupplierId = (cardQ.data as { defaultSupplierId?: string | null } | undefined)?.defaultSupplierId ?? null;
  useEffect(() => {
    if (open && defaultSupplierId) setSupplierId((cur) => cur || defaultSupplierId);
  }, [open, defaultSupplierId]);

  // Talep eden varsayılan = giriş yapan kullanıcı (elle değiştirilebilir).
  const myId = meQ.data?.user?.id ?? null;
  useEffect(() => { if (open && myId) setRequesterId((cur) => cur || myId); }, [open, myId]);

  const supplierOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);
  const userOptions = useMemo<SSOption[]>(() => (usersQ.data ?? []).map((u) => ({ value: u.id, label: u.name })), [usersQ.data]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!item) return;
    const n = qtyStr.trim() ? parseTrNumber(qtyStr) : NaN;
    if (!Number.isFinite(n) || n <= 0) return setErr("Geçerli bir miktar girin.");
    // Tedarikçi OPSİYONEL (RFQ): boşsa tedarikçisiz talep açılır, teklifler eklenip onaylanan seçilir.
    let unitPrice: number | undefined;
    if (unitPriceStr.trim()) {
      const up = parseTrNumber(unitPriceStr);
      if (!Number.isFinite(up) || up < 0) return setErr("Geçerli bir birim fiyat girin (TR biçimi: 12,50).");
      unitPrice = up;
    }
    const pin = paymentTermsToInput(pt);
    try {
      const res = await createMut.mutateAsync({
        items: [{ itemId: item.id, qty: n, supplierId: supplierId || null, unitPrice }],
        requestedById: requesterId || undefined,
        ...pin,
      });
      await qc.invalidateQueries();
      const c = res.created[0];
      setDonePo({ code: c?.code ?? "PO", id: c?.id ?? "" });
      onDone(`${c?.code ?? "PO"} taslak satın alma siparişi oluşturuldu (${item.name} · ${fmtQty(n)} ${item.uom}).`);
    } catch { /* hata banner'ı */ }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xxl"
      title="Satın Alma Talebi"
      subtitle={item ? `${item.name} · ${item.code}` : undefined}
      footer={donePo ? (
        <>
          <button type="button" className="btn-md secondary" onClick={() => window.open(`/print/satinalma-talebi?id=${donePo.id}`, "_blank", "noopener")}>
            <Icon name="printer" size={14} />A4 Yazdır
          </button>
          <button type="button" className="btn-md primary" onClick={onClose}>Kapat</button>
        </>
      ) : (
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
          <button type="submit" form="poreq-form" className="btn-md primary" disabled={createMut.isPending}>
            {createMut.isPending ? "Oluşturuluyor…" : "Taslak PO Oluştur"}
          </button>
        </>
      )}
    >
      {donePo ? (
        <div className="form-banner-ok">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Talep oluşturuldu</div>
            <div>Taslak satın alma siparişi: <span className="mono" style={{ fontWeight: 600 }}>{donePo.code}</span></div>
            <div style={{ marginTop: 6, fontSize: 12 }}>
              Onay, teklif ekleme ve mal kabul için <Link to="/purchasing" style={{ color: "var(--primary)" }}>Satınalma sayfasına</Link> gidin; ya da <b>A4 Yazdır</b> ile talebi çıktı alın.
            </div>
          </div>
        </div>
      ) : (
        <form id="poreq-form" onSubmit={handleSubmit} noValidate>
          {err || createMut.error ? <div className="form-banner-err" role="alert">{err ?? createMut.error?.message}</div> : null}

          {item ? (
            <div className="form-banner-info">
              Eldeki: <b className="mono">{fmtQty(item.onHand)} {item.uom}</b>
              {item.reorderPoint != null ? <> · Eşik: <b className="mono">{fmtQty(item.reorderPoint)} {item.uom}</b></> : " · Eşik tanımsız"}
              {suggestedQty != null ? <> · Önerilen sipariş: <b className="mono">{fmtQty(suggestedQty)} {item.uom}</b></> : null}
            </div>
          ) : null}

          {/* Sıkı ızgara: miktar + birim fiyat + tedarikçi + talep eden — tek sırada */}
          <div className="po-fgrid">
            <div className="form-field">
              <label className="lbl" htmlFor="poreq-qty">Sipariş Miktarı{item ? ` (${item.uom})` : ""} <span className="req">*</span></label>
              <input id="poreq-qty" className="form-input" type="text" inputMode="decimal" value={qtyStr}
                onChange={(e) => { setQtyStr(e.target.value.replace(/[^\d.,]/g, "")); setErr(null); }}
                placeholder="örn. 500" />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="poreq-price">Birim Fiyat</label>
              <input id="poreq-price" className="form-input" type="text" inputMode="decimal" value={unitPriceStr}
                onChange={(e) => { setUnitPriceStr(e.target.value.replace(/[^\d.,]/g, "")); setErr(null); }}
                placeholder="opsiyonel — örn. 12,50" />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="poreq-sup">Tedarikçi</label>
              <SearchSelect
                id="poreq-sup"
                value={supplierId || null}
                options={supplierOptions}
                onChange={(v) => { setSupplierId(v ?? ""); setErr(null); }}
                placeholder="— Tedarikçi (opsiyonel) —"
                searchPlaceholder="Tedarikçi ara…"
                clearable
              />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="poreq-req">Talep Eden</label>
              <SearchSelect
                id="poreq-req"
                value={requesterId || null}
                options={userOptions}
                onChange={(v) => setRequesterId(v ?? "")}
                placeholder="— Talep eden —"
                searchPlaceholder="Kullanıcı ara…"
              />
            </div>
          </div>
          <span className="help" style={{ display: "block", marginBottom: 10 }}>
            Tedarikçi opsiyonel — boş bırakılırsa teklifler eklenip onaylanan seçilir. Birim fiyat para birimi aşağıdan seçilir; talep eden varsayılan sizsiniz.
          </span>

          <PaymentTermsFields value={pt} onChange={setPt} enabled={open} />

          {/* Bu stok için geçmiş alım detayları (bilgi amaçlı) */}
          {item ? (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--neutral-500)", margin: "8px 0 4px" }}>Bu Ürünün Geçmiş Alımları</div>
              <PriceHistoryTable itemId={item.id} />
            </div>
          ) : null}

          <p style={{ fontSize: 12, color: "var(--neutral-500)", margin: "8px 0 0" }}>
            Talep, <b>taslak</b> satın alma siparişi (PO) olarak açılır — stok etkisi yoktur; teklifler eklenip onaylanan seçilince tedarikçi + fiyat işlenir, onaylanıp mal kabulü yapılınca stoğa girer.
          </p>
        </form>
      )}
    </Drawer>
  );
}
